import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { CheckCircle2 } from 'lucide-react';
import { projects } from '../data/mockData';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';

export const Gallery = () => {
  const customProducts = [
    {
      id: 1,
      title: 'Підземне сховище',
      description: 'Індивідуальне підземне сховище з високою міцністю та герметичністю для зберігання різних матеріалів',
      image: '/images/underground-storage.png',
      features: ['Підземна установка', 'Висока міцність', 'Повна герметичність']
    },
    {
      id: 2,
      title: 'Ємність з елементом підігріву',
      description: 'Спеціалізована ємність з вбудованим нагрівальним елементом для підтримки необхідної температури',
      image: '/images/container-heating.png',
      features: ['Вбудований підігрів', 'Термоізоляція', 'Контроль температури']
    },
    {
      id: 3,
      title: 'Резервуар для генератора',
      description: 'Компактний резервуар для паливної системи генератора з необхідними з\'єднаннями',
      image: '/images/generator-tank.png',
      features: ['Компактний дизайн', 'Спеціальні з\'єднання', 'Стійкість до палива']
    },
    {
      id: 4,
      title: 'Прямокутний складний резервуар',
      description: 'Багатокамерний прямокутний резервуар складної конфігурації для спеціальних технологічних процесів',
      image: '/images/rectangular-tank.png',
      features: ['Багатокамерна конструкція', 'Складна геометрія', 'Індивідуальне проєктування']
    },
    {
      id: 5,
      title: 'Піддон для зрошувальної системи',
      description: 'Спеціалізований піддон для систем зрошення з оптимізованою формою для рівномірного розподілу води',
      image: '/images/sprinkler-tray.png',
      features: ['Оптимальна форма', 'Рівномірний розподіл', 'Довговічність']
    },
    {
      id: 6,
      title: 'Спеціалізована ємність',
      description: 'Індивідуальна ємність складної форми для специфічних промислових процесів та технологічних потреб',
      image: '/images/custom-15.jpg',
      features: ['Унікальна конструкція', 'Промислове застосування', 'Індивідуальне виконання']
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Нестандартні пластикові вироби
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Під замовлення можемо виготовити нестандартні пластикові вироби за індивідуальними потребами та кресленнями замовника, 
            адаптуючи конструкцію під конкретні умови роботи й необхідні функції. Створюємо ємності та пристрої, що відповідають 
            навіть найскладнішим вимогам до розміру, форми й комплектації, причому проєктування завжди базується на аналізі 
            реальної ситуації на об'єкті.
          </p>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {customProducts.map((product) => (
              <Card
                key={product.id}
                className="overflow-hidden border-none shadow-lg hover:shadow-2xl transition-all duration-300 group"
              >
                <div className="relative h-80 overflow-hidden bg-white flex items-center justify-center p-6">
                  <img
                    src={product.image}
                    alt={product.title}
                    className="max-w-full max-h-full object-contain group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{product.title}</h3>
                  <p className="text-gray-600 text-sm mb-4 leading-relaxed">{product.description}</p>
                  <div className="space-y-2">
                    {product.features.map((feature, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <CheckCircle2 className="h-4 w-4 flex-shrink-0" style={{ color: 'rgb(1, 157, 180)' }} />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-none shadow-lg">
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Індивідуальний дизайн</h3>
                <p className="text-gray-600">
                  Устаткування проєктується після аналізу реальних умов на об'єкті: ми збираємо фактичні дані по стоках, 
                  режиму роботи та вимогах до очищення й на їх основі виконуємо технологічні розрахунки.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg">
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Специфіка виробництва</h3>
                <p className="text-gray-600">
                  Можемо виготовити обладнання в різних формах, розмірах і з потрібним набором функцій, щоб воно відповідало 
                  навіть найскладнішим вимогам замовника та ідеально підходило під конкретний об'єкт.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg">
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Матеріали</h3>
                <p className="text-gray-600">
                  Використовуємо високоякісний поліпропілен (PP) або поліетилен (PE), які мають високу міцність і стійкість 
                  до корозії та хімічного впливу. Обладнання служить довго та безпечно працює у складних умовах.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: 'rgb(1, 157, 180)' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Потрібен нестандартний виріб?
          </h2>
          <p className="text-lg text-white/90 mb-8">
            Звертайтесь до нас з вашими кресленнями та технічними вимогами. Ми створимо рішення, яке ідеально підійде для вашого об'єкту.
          </p>
          <Button asChild size="lg" variant="secondary" className="bg-white text-gray-900 hover:bg-gray-100">
            <Link to="/contacts">Зв'язатися з нами</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};